// src/utils/birthdayMessages.ts

interface BirthdayMessages {
  [key: string]: string[];
}

// Special messages for November 24 (Scorpio/Sagittarius cusp)
export const birthdayMessages: BirthdayMessages = {
  "11-24": [
    "✨ Secret Message: The universe has a special plan for you today. Trust the mysterious path unfolding before you.",
    "🌠 Secret Message: Your Scorpio intensity meets Sagittarius optimism - a powerful combination for manifesting your deepest desires.",
    "🔮 Secret Message: Those born on the cusp of transformation and adventure have a unique gift. Embrace both your depth and your wanderlust.",
    "💫 Secret Message: Today, your intuition is exceptionally strong. Listen to that quiet inner voice - it knows the way.",
    "🌟 Secret Message: You stand at a threshold between endings and beginnings. The cards show you're ready for what's next.",
    "✨ Secret Message: Your passion and honesty are your greatest assets. Don't be afraid to show the world your true self.",
    "🌙 Secret Message: The stars align for you tonight. Pay attention to signs and synchronicities - they're guiding you home.",
    "💫 Secret Message: You have the power to transform any situation. Trust your ability to rise from the ashes like a phoenix.",
  ],
};

// Helper function to parse date in multiple formats
export const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;

  console.log("Parsing date string:", dateStr);

  const preferredLanguage =
    typeof window !== "undefined"
      ? localStorage.getItem("preferredLanguage") || "en"
      : "en";

  // Try **/ **/ **** format (DD/MM/YYYY or MM/DD/YYYY)
  const ddmmyyyySlash = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  let match = dateStr.match(ddmmyyyySlash);
  if (match) {
    const part1 = parseInt(match[1], 10);
    const part2 = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    // Disambiguate:
    // - If part1 > 12 => must be DD/MM
    // - If part2 > 12 => must be MM/DD
    // - Else use preferred language (th => DD/MM, otherwise MM/DD)
    const assumeDmy =
      (part1 > 12 && part2 <= 12) ||
      (part1 <= 12 && part2 <= 12 && preferredLanguage === "th");

    const day = assumeDmy ? part1 : part2;
    const month = assumeDmy ? part2 : part1;

    console.log("Matched **/**/**** with slashes:", { day, month, year });
    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    ) {
      return date;
    }
  }

  // Try **-**-**** format (DD-MM-YYYY or MM-DD-YYYY)
  const ddmmyyyyDash = /^(\d{2})-(\d{2})-(\d{4})$/;
  match = dateStr.match(ddmmyyyyDash);
  if (match) {
    const part1 = parseInt(match[1], 10);
    const part2 = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    const assumeDmy =
      (part1 > 12 && part2 <= 12) ||
      (part1 <= 12 && part2 <= 12 && preferredLanguage === "th");

    const day = assumeDmy ? part1 : part2;
    const month = assumeDmy ? part2 : part1;

    console.log("Matched **-**-**** with dashes:", { day, month, year });
    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    ) {
      return date;
    }
  }

  // Try YYYY-MM-DD format (1990-11-24)
  const yyyymmdd = /^(\d{4})-(\d{2})-(\d{2})$/;
  match = dateStr.match(yyyymmdd);
  if (match) {
    const [_, year, month, day] = match;
    console.log("Matched YYYY-MM-DD:", { year, month, day });
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  // Fallback to native Date parsing
  console.log("Trying native Date parsing");
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date;
  }

  console.log("❌ Failed to parse date");
  return null;
};

// Function to get zodiac sign from date
export const getZodiacSign = (date: Date): string => {
  const month = date.getMonth() + 1;
  const day = date.getDate();

  if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return "aries";
  if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return "taurus";
  if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return "gemini";
  if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return "cancer";
  if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return "leo";
  if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return "virgo";
  if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return "libra";
  if ((month === 10 && day >= 23) || (month === 11 && day <= 21))
    return "scorpio";
  if ((month === 11 && day >= 22) || (month === 12 && day <= 21))
    return "sagittarius";
  if ((month === 12 && day >= 22) || (month === 1 && day <= 19))
    return "capricorn";
  if ((month === 1 && day >= 20) || (month === 2 && day <= 18))
    return "aquarius";
  if ((month === 2 && day >= 19) || (month === 3 && day <= 20)) return "pisces";

  return "unknown";
};

// Check if user qualifies for special message
export const qualifiesForSpecialMessage = (dob: string): boolean => {
  try {
    console.log("🔍 Checking if qualifies for:", dob);
    const date = parseDate(dob);
    if (!date) {
      console.log("❌ Could not parse date");
      return false;
    }

    const month = date.getMonth() + 1;
    const day = date.getDate();

    console.log("📅 Parsed date - Month:", month, "Day:", day);

    // Format as MM-DD for lookup
    const dateKey = `${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
    console.log("Date key:", dateKey);

    // Check for specific date (November 24)
    if (dateKey === "11-24") {
      console.log("✅ Exact date match for Nov 24!");
      return true;
    }

    // Check for zodiac cusp (Scorpio/Sagittarius: Nov 18-24)
    if (month === 11 && day >= 18 && day <= 24) {
      console.log("✅ Scorpio/Sagittarius cusp match!");
      return true;
    }

    console.log("❌ No special match");
    return false;
  } catch (error) {
    console.error("Error checking birthday:", error);
    return false;
  }
};

// Get random special message
export const getRandomSpecialMessage = (dob: string): string | null => {
  try {
    console.log("Getting special message for:", dob);
    const date = parseDate(dob);
    if (!date) {
      console.log("❌ Could not parse date");
      return null;
    }

    const month = date.getMonth() + 1;
    const day = date.getDate();

    // Check specific date first (November 24)
    if (month === 11 && day === 24) {
      console.log("✅ Getting message for Nov 24");
      const messages = birthdayMessages["11-24"];
      const randomIndex = Math.floor(Math.random() * messages.length);
      return messages[randomIndex];
    }

    // Check if Scorpio/Sagittarius cusp (Nov 18-24)
    if (month === 11 && day >= 18 && day <= 24) {
      console.log("✅ Getting message for cusp date");
      const messages = birthdayMessages["11-24"];
      const randomIndex = Math.floor(Math.random() * messages.length);
      return messages[randomIndex];
    }

    console.log("❌ No special message for this date");
    return null;
  } catch (error) {
    console.error("Error getting special message:", error);
    return null;
  }
};
